import { useEffect, useState } from 'react';
import { localStore, subscribeFailedEvents } from '../db';
import { subscribe as subscribeSync } from '../sync';
import { subscribeRetryStatus, listRetryingDocs } from './yjsCollab';

/**
 * Reactive sync-status snapshot for the header / status indicator.
 *
 * Combines four orthogonal signals into one shape:
 *   1. `pendingDocCount` / `pendingCustomerCount` — IndexedDB rows flagged
 *      `pendingSync:true`. Set by `persistSnapshot` (every retry iteration)
 *      and `upsertDocument` / `upsertCustomer`. Cleared on successful PUT.
 *   2. `outboxOpCount` — queued create/update/delete ops in the outbox.
 *      Cleared by `flushOutbox` on success; stays put on permanent failure
 *      (which surfaces a separate notification — see `onPermanentOutboxError`).
 *   3. `retryingDocCount` — Y.Docs currently in the persistSnapshot retry
 *      loop (transient errors, exponential backoff). User-visible as
 *      "Saving…" rather than "Pending".
 *   4. `lastSnapshotError` — the most recent retry's lastError, surfaced for
 *      tooltip / toast copy.
 *
 * Refreshes on every `notify()` from sync (enqueue, flush, pullAll) AND on
 * every retry-status change. IndexedDB reads are cheap; counts are just
 * `listDocuments().filter(...)`.
 */
export interface SyncStatus {
  pendingDocCount: number;
  pendingCustomerCount: number;
  outboxOpCount: number;
  retryingDocCount: number;
  /**
   * Sticky count of permanently-dropped sync events (outbox ops + snapshot
   * PUTs) since the user last acknowledged via the Recover-failed-edits
   * modal. Persisted in IndexedDB meta so an F5/refresh doesn't hide the
   * state. See plan F4 — this exists because the previous toast-only
   * notification could be missed and let users believe the green status
   * light meant "saved" when an op had silently been dropped.
   */
  permanentlyFailedCount: number;
  lastSnapshotError?: string;
}

const ZERO: SyncStatus = {
  pendingDocCount: 0,
  pendingCustomerCount: 0,
  outboxOpCount: 0,
  retryingDocCount: 0,
  permanentlyFailedCount: 0,
};

export function useSyncStatus(): SyncStatus {
  const [status, setStatus] = useState<SyncStatus>(ZERO);

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      try {
        const [docs, custs, ops, failed] = await Promise.all([
          localStore.listDocuments(),
          localStore.listCustomers(),
          localStore.listOps(),
          localStore.listFailedEvents(),
        ]);
        if (cancelled) return;
        const retrying = listRetryingDocs();
        const lastError = (() => {
          // Find any op with a stamped lastError to surface in the tooltip.
          for (const op of ops) {
            if ((op as any).lastError) return (op as any).lastError as string;
          }
          return undefined;
        })();
        setStatus({
          pendingDocCount: docs.filter((d) => d.pendingSync).length,
          pendingCustomerCount: custs.filter((c) => c.pendingSync).length,
          outboxOpCount: ops.length,
          retryingDocCount: retrying.length,
          permanentlyFailedCount: failed.length,
          lastSnapshotError: lastError,
        });
      } catch {
        /* ignore */
      }
    }
    void refresh();
    const unsubSync = subscribeSync(() => void refresh());
    const unsubRetry = subscribeRetryStatus(() => void refresh());
    const unsubFailed = subscribeFailedEvents(() => void refresh());
    return () => {
      cancelled = true;
      unsubSync();
      unsubRetry();
      unsubFailed();
    };
  }, []);

  return status;
}

/**
 * Total pending count — anything the user would describe as "not yet saved".
 * Combines doc/customer rows with pendingSync:true and the outbox queue.
 */
export function totalPending(s: SyncStatus): number {
  return s.pendingDocCount + s.pendingCustomerCount + s.outboxOpCount;
}
